import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endpoints, fmtTime } from "@/lib/api";
import { Bell, Mail, Terminal } from "lucide-react";

export default function NotificationsBell() {
  const [data, setData] = useState({ notifications: [], unread: 0, email_configured: false });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = () => endpoints.notifications().then(setData).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openItem = async (n) => {
    if (!n.read) await endpoints.markRead(n.id);
    setOpen(false);
    load();
    if (n.run_id) navigate(`/agent?run=${n.run_id}`);
  };

  const readAll = async () => { await endpoints.markAllRead(); load(); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="notifications-bell"
        className="relative w-8 h-8 border border-zinc-200 rounded-sm flex items-center justify-center hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
      >
        <Bell className="w-3.5 h-3.5" />
        {data.unread > 0 && (
          <span data-testid="notifications-unread" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-orange-600 text-white text-[9px] font-mono flex items-center justify-center rounded-sm">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-panel" className="absolute right-0 top-10 w-[380px] bg-white border border-zinc-200 shadow-2xl z-50">
          <div className="px-4 h-10 border-b border-zinc-200 flex items-center justify-between">
            <span className="tx-overline">Notifications</span>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] font-mono flex items-center gap-1 ${data.email_configured ? "text-emerald-600" : "text-amber-600"}`} data-testid="notifications-email-mode">
                {data.email_configured ? <Mail className="w-3 h-3" /> : <Terminal className="w-3 h-3" />}
                {data.email_configured ? "email live" : "log-only"}
              </span>
              {data.unread > 0 && (
                <button onClick={readAll} data-testid="notifications-read-all" className="text-[10px] font-mono text-zinc-500 hover:text-zinc-900">mark all read</button>
              )}
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-zinc-200">
            {data.notifications.length === 0 && (
              <div className="px-4 py-8 text-[12px] text-zinc-500 text-center">No notifications yet. Owners are alerted when a run reaches the approval gate.</div>
            )}
            {data.notifications.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid="notification-item"
                      className={`w-full text-left px-4 py-3 hover:bg-zinc-100 transition-colors ${n.read ? "opacity-60" : ""}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12px] text-orange-600 font-medium flex items-center gap-2">
                    {!n.read && <span className="w-1.5 h-1.5 bg-orange-500 rounded-full" />}
                    {n.title}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-500">{fmtTime(n.created_at)}</span>
                </div>
                <div className="text-[11px] text-zinc-600 line-clamp-2">{n.body}</div>
                <div className="font-mono text-[10px] text-zinc-400 mt-1">{n.run_id} · {n.email_status}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
